'use client'

import { useEffect, useMemo, useRef } from 'react'
import { type CategoryDTO, Currency, type Option, type ProductLightDTO } from 'ecosystem'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { overrideText } from 'ui/lib/overrides'
import { type CurrencyOverrides } from '../../types'
import { createCategoryMap, ga4EcommerceItemFactoryFromProducts } from '../utils'
import { useGtmEvents } from './useGtmEvents'

interface TrackViewItemListParams {
  categories: Option<CategoryDTO[]>
  products: ProductLightDTO[] | undefined
  id: string
  listName?: string
  currency?: Currency
  isLoading?: boolean
}

export const useGtmViewItemListTracker = ({
  categories,
  products,
  id,
  listName,
  currency = Currency.SEK,
  isLoading = false
}: TrackViewItemListParams) => {
  const overrides = useOverridesContext<keyof CurrencyOverrides>()
  const { trackViewItemListEvent } = useGtmEvents()

  const previousTrackingKeyRef = useRef<string | null>(null)
  const previousIsLoadingRef = useRef<boolean>(false)

  const categoryMap = useMemo(() => createCategoryMap(categories), [categories])

  useEffect(() => {
    previousTrackingKeyRef.current = null
    previousIsLoadingRef.current = false
  }, [id])

  useEffect(() => {
    const wasLoading = previousIsLoadingRef.current
    const justFinishedLoading = wasLoading && !isLoading
    previousIsLoadingRef.current = isLoading

    if (isLoading) return
    if (products === undefined) return
    if (products.length === 0 && !justFinishedLoading) return

    const trackingKey = JSON.stringify({
      length: products.length,
      ids: products.map((p) => p.id),
      searchId: id
    })

    if (trackingKey !== previousTrackingKeyRef.current) {
      const isTracked = trackViewItemListEvent({
        ecommerce: {
          currency: overrideText(currency, overrides?.currency) as Currency,
          item_list_id: id,
          item_list_name: listName,
          items: ga4EcommerceItemFactoryFromProducts(products, categoryMap)
        }
      })

      if (isTracked) {
        previousTrackingKeyRef.current = trackingKey
      }
    }
  }, [
    products,
    id,
    listName,
    currency,
    overrides?.currency,
    isLoading,
    categories,
    trackViewItemListEvent,
    categoryMap
  ])
}
